import { c as _c } from "react/compiler-runtime";
import React, { ReactNode, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { usePositionMarker } from './position-marker-context';

/**
 * Props for the PositionMarkerContainer component
 */
interface PositionMarkerContainerProps {
  /** Child component to track */
  children: ReactNode;
  /** Threshold percentage (0-1) for when component is considered out of view. Default: 0.1 (10%) */
  threshold?: number;
  /** Additional CSS class name */
  className?: string;
}

/**
 * Container component that tracks when its' child component enters/exits the viewport
 * Uses intersection observer to detect visibility changes and updates the position marker context
 */
export function PositionMarkerContainer(t0) {
  const $ = _c(11);
  const {
    children,
    threshold: t1,
    className
  } = t0;
  const threshold = t1 === undefined ? 0.1 : t1;
  const {
    setComponentOutOfView
  } = usePositionMarker();
  const isPageVisible = useRef(true);
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const handleVisibilityChange = () => {
        isPageVisible.current = !document.hidden;
      };
      if (typeof document !== "undefined") {
        isPageVisible.current = !document.hidden;
        document.addEventListener("visibilitychange", handleVisibilityChange);
      }
      return () => {
        if (typeof document !== "undefined") {
          document.removeEventListener("visibilitychange", handleVisibilityChange);
        }
      };
    };
    t3 = [];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useEffect(t2, t3);
  let t4;
  if ($[2] !== setComponentOutOfView) {
    t4 = inView => {
      if (isPageVisible.current) {
        setComponentOutOfView(inView);
      }
    };
    $[2] = setComponentOutOfView;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t4 || $[5] !== threshold) {
    t5 = {
      threshold,
      triggerOnce: false,
      onChange: t4
    };
    $[4] = t4;
    $[5] = threshold;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const {
    ref
  } = useInView(t5);
  let t6;
  if ($[7] !== children || $[8] !== className || $[9] !== ref) {
    t6 = <div ref={ref} className={className}>{children}</div>;
    $[7] = children;
    $[8] = className;
    $[9] = ref;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}